import { Emitter } from "../core/emitter";
import { errorMessage } from "../core/errors";
import { parseMidiMessage, panicMessages, type MidiEvent } from "./parse";

export type MidiStatus =
  | { state: "idle" }
  | { state: "unsupported"; reason: string }
  | { state: "insecure"; reason: string }
  | { state: "requesting" }
  | { state: "denied"; reason: string }
  | { state: "error"; reason: string }
  | { state: "ready" };

export interface MidiPortInfo {
  id: string;
  name: string;
  manufacturer: string;
  kind: "input" | "output";
  connected: boolean;
}

export interface MidiMessageEvent {
  portId: string;
  portName: string;
  event: MidiEvent;
  raw: number[];
  timeStamp: number;
}

/** Subset of the Web MIDI API we depend on (eases faking in tests). */
export interface MidiEnvironment {
  isSecureContext: boolean;
  requestMIDIAccess?: (opts?: { sysex?: boolean }) => Promise<MIDIAccess>;
}

export function browserMidiEnvironment(): MidiEnvironment {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  return {
    isSecureContext: typeof globalThis.isSecureContext === "boolean" ? globalThis.isSecureContext : true,
    requestMIDIAccess: nav && "requestMIDIAccess" in nav ? nav.requestMIDIAccess.bind(nav) : undefined,
  };
}

/**
 * Wraps Web MIDI with explicit capability detection and permission handling.
 * Never throws to callers: failures become `status` (AGENT.md §9).
 */
export class MidiService {
  readonly statusChanged = new Emitter<MidiStatus>();
  readonly devicesChanged = new Emitter<MidiPortInfo[]>();
  readonly messages = new Emitter<MidiMessageEvent>();
  /** Fires with the port id when an input disconnects (to release stuck notes). */
  readonly inputDisconnected = new Emitter<string>();

  private access: MIDIAccess | null = null;
  private _status: MidiStatus = { state: "idle" };
  private _devices: MidiPortInfo[] = [];
  private disabledInputs = new Set<string>();

  constructor(private env: MidiEnvironment = browserMidiEnvironment()) {
    if (!env.requestMIDIAccess) {
      this._status = { state: "unsupported", reason: "This browser does not provide the Web MIDI API (e.g. Safari). Use Chrome, Edge or Firefox on desktop." };
    } else if (!env.isSecureContext) {
      this._status = { state: "insecure", reason: "Web MIDI requires a secure context (HTTPS or localhost)." };
    }
  }

  get status(): MidiStatus { return this._status; }
  get devices(): MidiPortInfo[] { return this._devices; }

  get supported(): boolean {
    return this._status.state !== "unsupported" && this._status.state !== "insecure";
  }

  /** Request access (no SysEx). Safe to call repeatedly. */
  async request(): Promise<MidiStatus> {
    if (!this.supported || !this.env.requestMIDIAccess) return this._status;
    if (this._status.state === "ready") return this._status;
    this.setStatus({ state: "requesting" });
    try {
      const access = await this.env.requestMIDIAccess({ sysex: false });
      this.access = access;
      access.onstatechange = () => this.refresh();
      this.refresh();
      this.setStatus({ state: "ready" });
    } catch (err) {
      const name = err instanceof DOMException || (err instanceof Error) ? err.name : "";
      if (name === "SecurityError" || name === "NotAllowedError") {
        this.setStatus({ state: "denied", reason: "MIDI permission was denied. Allow MIDI for this site in the browser's site settings (Firefox asks to install a site-permission add-on)." });
      } else {
        this.setStatus({ state: "error", reason: `MIDI could not be started: ${errorMessage(err)}` });
      }
    }
    return this._status;
  }

  setInputEnabled(id: string, enabled: boolean): void {
    if (enabled) this.disabledInputs.delete(id);
    else this.disabledInputs.add(id);
    this.refresh();
  }

  isInputEnabled(id: string): boolean {
    return !this.disabledInputs.has(id);
  }

  /** Send to one output port. `timestamp` is DOMHighResTimeStamp (performance.now() base). */
  send(outputId: string, data: number[], timestamp?: number): boolean {
    const out = this.access?.outputs.get(outputId);
    if (!out || out.state !== "connected") return false;
    out.send(data, timestamp);
    return true;
  }

  /** CC120 + CC123 on all channels of every connected output. Returns messages sent. */
  panicOutputs(): number {
    if (!this.access) return 0;
    let sent = 0;
    this.access.outputs.forEach((out) => {
      if (out.state !== "connected") return;
      for (const msg of panicMessages()) { out.send(msg); sent++; }
    });
    return sent;
  }

  private refresh(): void {
    if (!this.access) return;
    const prev = new Map(this._devices.map((d) => [d.id, d]));
    const list: MidiPortInfo[] = [];
    this.access.inputs.forEach((input) => {
      const connected = input.state === "connected";
      list.push({ id: input.id, name: input.name ?? "Unknown input", manufacturer: input.manufacturer ?? "", kind: "input", connected });
      if (connected && this.isInputEnabled(input.id)) {
        input.onmidimessage = (ev: MIDIMessageEvent) => {
          if (!ev.data) return;
          const raw = Array.from(ev.data);
          this.messages.emit({ portId: input.id, portName: input.name ?? "", event: parseMidiMessage(raw), raw, timeStamp: ev.timeStamp });
        };
      } else {
        input.onmidimessage = null;
      }
      if (prev.get(input.id)?.connected && (!connected || !this.isInputEnabled(input.id))) this.inputDisconnected.emit(input.id);
    });
    this.access.outputs.forEach((out) => {
      list.push({ id: out.id, name: out.name ?? "Unknown output", manufacturer: out.manufacturer ?? "", kind: "output", connected: out.state === "connected" });
    });
    this._devices = list;
    this.devicesChanged.emit(list);
  }

  private setStatus(s: MidiStatus): void {
    this._status = s;
    this.statusChanged.emit(s);
  }

  dispose(): void {
    if (this.access) {
      this.access.onstatechange = null;
      this.access.inputs.forEach((i) => { i.onmidimessage = null; });
    }
    this.access = null;
  }
}
